import { apiClient } from '@/services/apiClient'
import type {
  ApiResponse,
  AreaAvailabilityParams,
  AreaAvailabilityResult,
  Reservation,
  ReservationListItem,
  ReservationListQuery,
  ReservationAuthSession,
  ReservationCreateRequest,
  ReservationGuestRequest,
  PaginatedReservationResponse,
  ReservationRequest,
  ReservedTable,
  User,
  JoinActiveOrderResult,
} from '@/interfaces'

export const reservationApi = {
  async getReservationList(
    params: ReservationListQuery = {}
  ): Promise<ApiResponse<ReservationListItem[] | PaginatedReservationResponse>> {
    const response = await apiClient.get('customer/reservation_list', {
      params: {
        sort: '',
        page: 1,
        per_page: 10,
        ...params,
      },
    })
    return response.data
  },

  async createReservation(data: ReservationCreateRequest): Promise<ApiResponse<Reservation>> {
    const response = await apiClient.post('customer/reservation_create', data)
    return response.data
  },

  async getAvailableTimes(params: {
    area_id: string | number
    date: string
    tenant_id: string
  }): Promise<ApiResponse<unknown>> {
    const response = await apiClient.get('customer/available_times', { params })
    return response.data
  },

  async getReservedTables(params: { tenant_id: string }): Promise<ApiResponse<ReservedTable[]>> {
    const response = await apiClient.get('customer/reserved_tables', { params })
    return response.data
  },

  async checkReservationNumberValidity(params: {
    reservation_number: string
    tenant_id: string
    table_no: string
  }): Promise<ApiResponse<Reservation>> {
    const response = await apiClient.post('customer/check_reservation_number_validity', params)
    return response.data
  },

  /**
   * Join an already-active shared order using its reservation PIN.
   * Requires the current logged-in user's bearer token.
   */
  async joinActiveOrder(params: {
    tenant_id: string
    reservation_number: string
    table_no?: string
  }): Promise<ApiResponse<JoinActiveOrderResult>> {
    const response = await apiClient.post('customer/join_active_order', params)
    return response.data
  },

  /**
   * Make instant reservation for NOT logged-in users.
   * Send mobile_no (+ first_name, last_name, email for new users). Get tenant_id and table_no from menu URL/context.
   * Backend: if mobile is registered → logs in user and creates reservation; if not in DB → creates user, logs in, creates reservation.
   * Response: { data: { customer_access_token, expires_at, user_data, active_reservation_id }, success, code: 200 }
   */
  async makeInstantReservation(data: ReservationRequest): Promise<ApiResponse<Reservation>> {
    const response = await apiClient.post('customer/make_instant_reservation', data)
    return response.data
  },

  /**
   * Make instant reservation for LOGGED-IN users only.
   * Uses the current logged-in user to create the instant reservation (tenant_id, table_no from URL/context).
   */
  async makeInstantRegisteredReservation(
    data: ReservationRequest
  ): Promise<ApiResponse<Reservation>> {
    const response = await apiClient.post('customer/make_instant_registered_reservation', data)
    return response.data
  },

  async createReservationOtp(data: ReservationGuestRequest): Promise<ApiResponse> {
    const response = await apiClient.post('customer/otp/create_reservation', data)
    return response.data
  },

  async checkCustomerMobileForReservation(data: ReservationGuestRequest): Promise<ApiResponse> {
    const response = await apiClient.post('customer/check_customer_mobile_reservation', data)
    return response.data
  },

  async generateAfterReservationToken(
    data: ReservationGuestRequest
  ): Promise<ApiResponse<ReservationAuthSession>> {
    const response = await apiClient.post('customer/generate_after_reservation_token', data)
    return response.data
  },

  /**
   * Validate active reservation is still open.
   * Request body: { active_reservation_id: number }. Returns 200 on success.
   * Use `{ bearerToken }` with `customer_access_token` from PIN validity before the session is in the store.
   * Use `{ skipAuth: true }` only if the endpoint truly accepts no auth.
   */
  async checkActiveReservation(
    params: { active_reservation_id: number },
    options?: { skipAuth?: boolean; bearerToken?: string }
  ): Promise<
    ApiResponse<{
      id?: string
      active_reservation_id?: number
      tenant_id?: string
      table_no?: string
      show_table_available?: boolean
      status?: string
      reservation_number?: string
      reservation_numer?: string
    }>
  > {
    const response = await apiClient.post('customer/check_active_reservation', params, {
      ...(options?.skipAuth === true ? { skipAuth: true } : {}),
      ...(options?.bearerToken ? { bearerToken: options.bearerToken } : {}),
    })
    return response.data
  },

  /**
   * Lightweight validation endpoint for active reservation checks.
   * Request body: { active_reservation_id: number }. Returns top-level { success, code }.
   * Use this when we only need to know whether the reservation is still active.
   */
  async checkActiveReservationNew(
    params: { active_reservation_id: number },
    options?: { skipAuth?: boolean; bearerToken?: string }
  ): Promise<ApiResponse> {
    const response = await apiClient.post('customer/check_active_reservation_new', params, {
      ...(options?.skipAuth === true ? { skipAuth: true } : {}),
      ...(options?.bearerToken ? { bearerToken: options.bearerToken } : {}),
    })
    return response.data
  },

  async checkReservationHost(params: { active_reservation_id: number }): Promise<ApiResponse> {
    const response = await apiClient.post('customer/check_reservation_host', params)
    return response.data
  },

  async validateReservationNumber(params: { reservation_number: string }): Promise<
    ApiResponse<{
      active_reservation: {
        tenant_id: string
        table_no: string
        reservation_numer: string
        active_reservation_id: number
        customer_access_token: string
        expires_at: string
      }
      user_data: User
    }>
  > {
    // This endpoint should accept just reservation_number
    // Backend returns tenant_id, table_no, and logs user in
    const response = await apiClient.post('customer/validate_reservation_number', params)
    return response.data
  },

  /**
   * Check available tables and seats for a specific area, date, time, and duration.
   * TODO: Wire to the actual endpoint once available.
   * Expected endpoint: GET customer/area_availability
   * Response: { data: { available_tables: number, available_seats: number } }
   */
  async getAreaAvailability(
    params: AreaAvailabilityParams
  ): Promise<ApiResponse<AreaAvailabilityResult>> {
    const response = await apiClient.get('customer/area_availability', { params })
    return response.data
  },
}
